import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Coin } from '@/components/ui';
import { BookingTypeIcon } from '@/components/companion/ServiceIcon';
import BookingDialog from './BookingDialog';
import { bookingTypeLabel, unitLabel, type Rate, type ServiceListing } from '@/lib/data/services';
import { rupees, type WalletState } from '@/lib/data/wallet';

/**
 * The booking panel.
 *
 * Implements the two gates project.md puts in front of every booking:
 *
 *   §15  Has the user paid the one-time platform fee?
 *   §16  Do they have enough coins?
 *
 * Checked in that order, because that is the order they block in — someone
 * with no fee paid and no coins should be told about the fee first, not sent
 * to top up and then stopped again.
 *
 * Booking itself is Block B. Every button here is disabled with an honest
 * reason rather than being hidden: a panel that shows what something costs and
 * why you cannot have it yet is more useful than one that shows nothing.
 */
export default function BookPanel({
  services,
  wallet,
  available,
  isBusy,
}: {
  services: ServiceListing[];
  wallet: WalletState | null;
  available: boolean;
  isBusy: boolean;
}) {
  const navigate = useNavigate();
  const [chosen, setChosen] = useState<{ service: ServiceListing; rate: Rate } | null>(null);

  const balance = wallet?.balanceCoins ?? 0;
  const feePaid = wallet?.fees.userFeePaid ?? false;
  const feePaise = wallet?.fees.userFeePaise ?? 0;

  // Every way this person can be booked, cheapest first.
  const options = services
    .flatMap((s) => s.rates.map((r) => ({ service: s, rate: r })))
    .sort((a, b) => a.rate.priceCoins - b.rate.priceCoins);

  return (
    <Card raised className="overflow-hidden">
      <div
        className="flex items-baseline justify-between px-5 py-4"
        style={{ background: 'var(--marigold-soft)' }}
      >
        <h2 className="font-semibold">Book</h2>
        <span className="inline-flex items-center gap-1.5 text-sm">
          <Coin className="text-[var(--marigold)]" />
          <span className="font-semibold tabular-nums">{balance.toLocaleString('en-IN')}</span>
        </span>
      </div>

      {/* Gate 1 — §15. Shown before anything else, because no amount of
          topping up gets past it. */}
      {!feePaid && feePaise > 0 && (
        <div className="border-b border-[var(--line-soft)] px-5 py-4">
          <p className="text-sm font-medium">Pay the one-time fee first</p>
          <p className="mt-1 text-xs leading-relaxed text-[var(--ink-2)]">
            {rupees(feePaise)}, paid once. It unlocks booking anyone on the platform — coins are
            separate and cover what you actually use.
          </p>
          <div className="mt-3">
            <Button to="/wallet" size="sm" full>
              Pay {rupees(feePaise)}
            </Button>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2.5 p-5">
        {options.length === 0 ? (
          <p className="py-4 text-center text-sm text-[var(--ink-2)]">
            Nothing listed to book yet.
          </p>
        ) : (
          options.map(({ service, rate }) => (
            <BookOption
              key={`${service.id}-${rate.bookingType}`}
              service={service}
              rate={rate}
              balance={balance}
              feePaid={feePaid || feePaise === 0}
              available={available}
              onChoose={() => setChosen({ service, rate })}
            />
          ))
        )}

        {!available && options.length > 0 && (
          <p
            className="mt-1 rounded-xl px-4 py-3 text-sm"
            style={{ background: 'var(--busy-soft)', color: 'var(--busy)' }}
          >
            {isBusy
              ? 'In a session right now — usually free again within 15 minutes.'
              : 'Offline at the moment. Come back this evening.'}
          </p>
        )}
      </div>

      <div className="border-t border-[var(--line-soft)] px-5 py-4">
        <Button to="/wallet" variant="secondary" full size="sm">
          Add coins
        </Button>
        <p className="mt-3.5 text-xs leading-relaxed text-[var(--muted)]">
          Calls are charged by the minute and refunded for time you did not use. Meeting in person
          is charged by the hour, agreed before you book.
        </p>
      </div>

      {chosen && (
        <BookingDialog
          service={chosen.service}
          rate={chosen.rate}
          balance={balance}
          onClose={() => setChosen(null)}
          onBooked={() => {
            setChosen(null);
            // Straight to the list, where the request now sits waiting. The
            // alternative — a toast on a page that looks unchanged — leaves
            // people unsure whether anything happened.
            navigate('/bookings');
          }}
        />
      )}
    </Card>
  );
}

function BookOption({
  service,
  rate,
  balance,
  feePaid,
  available,
  onChoose,
}: {
  service: ServiceListing;
  rate: Rate;
  balance: number;
  feePaid: boolean;
  available: boolean;
  onChoose: () => void;
}) {
  const perHour = rate.unit === 'per_hour';

  // What the balance buys. For a call that is minutes; for an in-person
  // booking it is whether one hour — the minimum — is affordable at all.
  const units = Math.floor(balance / rate.priceCoins);
  const affordable = perHour ? units >= 1 : units >= 1;

  /*
   * One reason, in the order the gates actually apply. Showing "top up" to
   * someone who has not paid the platform fee sends them to solve the wrong
   * problem.
   *
   * Presence does NOT block an in-person booking: those are scheduled for a
   * future date, so whether the companion happens to be online right now is
   * irrelevant. Only a live call needs them present.
   */
  const needsPresence = !perHour;

  const blocked = !feePaid
    ? 'fee first'
    : !affordable
      ? 'top up'
      : needsPresence && !available
        ? 'offline'
        : null;

  return (
    <button
      type="button"
      disabled={blocked !== null}
      onClick={onChoose}
      className="group flex items-center gap-3.5 rounded-xl border border-[var(--line)] px-4 py-3.5 text-left transition-all duration-150 enabled:hover:-translate-y-0.5 enabled:hover:border-[var(--marigold)] enabled:hover:bg-[var(--marigold-soft)] disabled:cursor-not-allowed"
      style={{ background: 'var(--surface)', opacity: blocked ? 0.5 : 1 }}
    >
      <span
        className="grid size-9 shrink-0 place-items-center rounded-lg"
        style={{ background: 'var(--surface-2)', color: 'var(--marigold)' }}
      >
        <BookingTypeIcon type={rate.bookingType} size={18} />
      </span>

      <span className="min-w-0 flex-1">
        <span className="block truncate font-semibold">{service.categoryName}</span>
        <span className="block text-xs text-[var(--muted)]">
          {bookingTypeLabel(rate.bookingType)}
        </span>
      </span>

      <span className="shrink-0 text-right">
        <span className="flex items-center justify-end gap-1 font-semibold tabular-nums">
          <Coin className="text-[var(--marigold)]" />
          {rate.priceCoins}
          <span className="text-xs font-normal text-[var(--muted)]">{unitLabel(rate.unit)}</span>
        </span>
        <span className="block text-xs text-[var(--muted)] tabular-nums">
          {blocked ?? (perHour ? `≈ ${units} hr` : `≈ ${units} min`)}
        </span>
      </span>
    </button>
  );
}
